'use client'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import { cn } from '@/lib/utils'
import Autoplay from 'embla-carousel-autoplay'
import {
  CollectionMixMatchV2Item,
  TMixMatchV2Look,
} from './collection-mix-match-v2-item'

type Props = {
  title: React.ReactNode
  data: TMixMatchV2Look[]
  /** Prefix ga-tracking-value cho từng look, vd `ldp-thu-dong__mix-match` */
  trackingValue?: string
  className?: string
  titleClassName?: string
}

/**
 * Mix & match V2 — desktop lưới 4 cột (không dùng Carousel), mobile slide (card 260px).
 * Hai layout ẩn/hiện bằng CSS để không lệch hydration; ảnh lazy nên bản bị ẩn không tải ảnh.
 * Look chưa gắn productIds sẽ bị ẩn để không mở modal rỗng.
 */
export const CollectionMixMatchV2: React.FC<Props> = ({
  title,
  data,
  trackingValue,
  className,
  titleClassName,
}) => {
  const getTracking = (index: number) =>
    trackingValue ? `${trackingValue}__${index + 1}` : undefined

  return (
    <section
      className={cn(
        'flex flex-col gap-5 px-4 py-10 lg:gap-10 lg:px-15 lg:py-20',
        className
      )}
    >
      <h2
        className={cn(
          'font-criteria text-[28px] font-semibold leading-[41px] tracking-[-0.02em] text-black lg:text-[55px] lg:font-medium lg:leading-[76px]',
          titleClassName
        )}
      >
        {title}
      </h2>

      {/* Desktop: lưới 4 cột */}
      <div className='grid grid-cols-4 gap-5 max-lg:hidden'>
        {data.map((look, index) => (
          <CollectionMixMatchV2Item
            key={`${look.thumbnail}-${index}`}
            {...look}
            trackingValue={getTracking(index)}
          />
        ))}
      </div>

      {/* Mobile: slide, card 260px */}
      <Carousel
        opts={{ align: 'start', loop: true }}
        plugins={[
          Autoplay({
            delay: 4000,
            stopOnInteraction: false,
          }),
        ]}
        className='-mr-4 lg:hidden'
      >
        <CarouselContent className='-ml-3'>
          {data.map((look, index) => (
            <CarouselItem
              key={`${look.thumbnail}-${index}`}
              className='basis-[272px] pl-3'
            >
              <CollectionMixMatchV2Item
                {...look}
                trackingValue={getTracking(index)}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </section>
  )
}
